import "@/i18n";
import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import SubdomainRouter from "@/components/SubdomainRouter";
import { CoachProvider } from "@/contexts/CoachContext";
import CustomHeadScript from "./components/CustomHeadScript";
import CookieConsent from "./components/CookieConsent";
import ConsentAwareScripts from "./components/ConsentAwareScripts";
import AnalyticsTracker from "./components/AnalyticsTracker";
import MarketingTheme from "@/components/MarketingTheme";

// Retry wrapper for lazy imports — handles stale chunk errors after deploys
function lazyRetry<T extends { default: React.ComponentType<any> }>(
  factory: () => Promise<T>
): React.LazyExoticComponent<T["default"]> {
  return lazy(() =>
    factory().catch((err) => {
      // If chunk failed to load (stale hash), try one reload
      const key = "chunk-reload-retry";
      const hasRetried = sessionStorage.getItem(key);
      if (!hasRetried) {
        sessionStorage.setItem(key, "1");
        window.location.reload();
        return new Promise(() => {}); // never resolves — page is reloading
      }
      sessionStorage.removeItem(key);
      throw err; // surface the real error after one retry
    })
  );
}

// Lazy load route components for code splitting
const PlatformHome = lazyRetry(() => import("./pages/PlatformHome"));
const Index = lazyRetry(() => import("./pages/Index"));
const NotFound = lazyRetry(() => import("./pages/NotFound"));
const CoachAuth = lazyRetry(() => import("./pages/CoachAuth"));
const CoachAdmin = lazyRetry(() => import("./pages/CoachAdmin"));
const CoachSite = lazyRetry(() => import("./pages/CoachSite"));
const SuperAdminAuth = lazyRetry(() => import("./pages/SuperAdminAuth"));
const SuperAdmin = lazyRetry(() => import("./pages/SuperAdmin"));
const Blog = lazyRetry(() => import("./pages/Blog"));
const BlogPost = lazyRetry(() => import("./pages/BlogPost"));
const Podcasts = lazyRetry(() => import("./pages/Podcasts"));
const PodcastPost = lazyRetry(() => import("./pages/PodcastPost"));
const Courses = lazyRetry(() => import("./pages/Courses"));
const CourseDetail = lazyRetry(() => import("./pages/CourseDetail"));
const ToolkitHub = lazyRetry(() => import("./pages/ToolkitHub"));
const ToolkitTool = lazyRetry(() => import("./pages/ToolkitTool"));
const PrivacyPolicy = lazyRetry(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazyRetry(() => import("./pages/TermsOfService"));
const AboutPage = lazyRetry(() => import("./pages/AboutPage"));
const ContactPage = lazyRetry(() => import("./pages/ContactPage"));
const Press = lazyRetry(() => import("./pages/Press"));
const PressRelease = lazyRetry(() => import("./pages/PressRelease"));
const SearchPage = lazyRetry(() => import("./pages/SearchPage"));
const Pricing = lazyRetry(() => import("./pages/Pricing"));
const SuccessStories = lazyRetry(() => import("./pages/SuccessStories"));
const CoachesDirectory = lazyRetry(() => import("./pages/CoachesDirectory"));
const CategoryHubPage = lazyRetry(() => import("./pages/CategoryHubPage"));
const InstitutePartners = lazyRetry(() => import("./pages/InstitutePartners"));
const OAuthConsent = lazyRetry(() => import("./pages/OAuthConsent"));
const ConnectAgent = lazyRetry(() => import("./pages/ConnectAgent"));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="text-center">
      <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-3" />
      <p className="text-sm text-muted-foreground">Loading...</p>
    </div>
  </div>
);

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <CustomHeadScript />
      <ConsentAwareScripts />
      <BrowserRouter>
        <AnalyticsTracker />
        <MarketingTheme />
        <CookieConsent />
        <SubdomainRouter>
          <CoachProvider coachId={null}>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<PlatformHome />} />
                <Route path="/demo" element={<Index />} />
                {/* Essential pages for AdSense & transparency */}
                <Route path="/about" element={<AboutPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/press" element={<Press />} />
                <Route path="/press/:slug" element={<PressRelease />} />
                <Route path="/search" element={<SearchPage />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/success-stories" element={<SuccessStories />} />
                <Route path="/coaches" element={<CoachesDirectory />} />
                <Route path="/partners" element={<InstitutePartners />} />
                <Route path="/institute-partners" element={<InstitutePartners />} />
                <Route path="/coaching-techniques" element={<CategoryHubPage />} />
                <Route path="/spiritual-coaching" element={<CategoryHubPage />} />
                <Route path="/coaching-business" element={<CategoryHubPage />} />
                <Route path="/personal-development" element={<CategoryHubPage />} />
                {/* Blog routes */}
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                {/* Podcast routes */}
                <Route path="/podcast" element={<Podcasts />} />
                <Route path="/podcast/:slug" element={<PodcastPost />} />
                {/* Courses routes */}
                <Route path="/courses" element={<Courses />} />
                <Route path="/courses/:slug" element={<CourseDetail />} />
                {/* Toolkit routes */}
                <Route path="/toolkit" element={<ToolkitHub />} />
                <Route path="/toolkit/:slug" element={<ToolkitTool />} />
                {/* Coach routes */}
                <Route path="/coach/auth" element={<CoachAuth />} />
                <Route path="/coach" element={<CoachAdmin />} />
                <Route path="/coach/admin" element={<CoachAdmin />} />
                {/* Super Admin routes */}
                <Route path="/admin/auth" element={<SuperAdminAuth />} />
                <Route path="/admin" element={<SuperAdmin />} />
                {/* MCP OAuth consent */}
                <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
                {/* Agent connection instructions */}
                <Route path="/connect" element={<ConnectAgent />} />
                {/* Coach websites via path - e.g., /coach-name */}
                <Route path="/:slug" element={<CoachSite />} />
              </Routes>
            </Suspense>
          </CoachProvider>
        </SubdomainRouter>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
